import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Hammer, FileText, Phone, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { api, resolveImg } from "@/lib/api";
import { COMPANY } from "@/data/content";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(undefined); // undefined = loading, null = not found
  const [others, setOthers] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/projects/${slug}`)
      .then((res) => {
        setProject(res.data);
        document.title = `${res.data.title} – ${res.data.location} | Klusbedrijf Pruijn`;
      })
      .catch(() => setProject(null));
    api.get("/projects").then((res) => setOthers(res.data.filter((p) => p.slug !== slug))).catch(() => {});
  }, [slug]);

  if (project === undefined) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white/40 gap-2">
        <Loader2 className="animate-spin" size={20} /> Laden...
      </div>
    );
  }

  if (project === null) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center gap-6 text-center px-6">
        <h1 className="font-heading text-3xl text-white">Project niet gevonden</h1>
        <Link to="/" className="text-[#FF1A1A] hover:underline" data-testid="back-home-link">Terug naar home</Link>
      </div>
    );
  }

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="project-detail-page">
      <Navbar />

      <section className="relative pt-24 md:pt-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <button onClick={() => navigate("/")} data-testid="back-button" className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors">
            <ArrowLeft size={18} /> Terug naar projecten
          </button>
        </div>
        <div className="max-w-xl mx-auto px-6">
          <div className="relative w-full aspect-[4/3] overflow-hidden bg-[#0a0a0a] rounded-lg border border-white/10">
            <img src={resolveImg(project.cover)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-3xl opacity-25" />
            <img src={resolveImg(project.cover)} alt={project.title} className="relative z-10 w-full h-full object-contain" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-8">
          <div className="flex items-center gap-2 text-[#FF1A1A] mb-3">
            <MapPin size={16} /> <span className="text-sm font-semibold uppercase tracking-wide">{project.location}</span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-white tracking-tight">{project.title}</h1>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6">Over dit project</h2>
            <p className="text-white/65 font-light text-lg leading-relaxed whitespace-pre-wrap">{project.description}</p>
          </div>
          <div className="bg-[#111111] p-8 border border-white/5 h-max">
            <div className="flex flex-col gap-6">
              <div>
                <div className="flex items-center gap-2 text-[#FF1A1A] mb-2"><MapPin size={16} /><span className="text-xs uppercase tracking-widest">Locatie</span></div>
                <p className="text-white">{project.location}</p>
              </div>
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-center gap-2 text-[#FF1A1A] mb-2"><Hammer size={16} /><span className="text-xs uppercase tracking-widest">Werkzaamheden</span></div>
                <p className="text-white">{project.work}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {project.before && project.after && (
        <section className="pb-20 md:pb-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">Voor &amp; na</h2>
            <p className="text-white/50 mt-2">Sleep de schuif om het verschil te zien.</p>
          </div>
          <div className="px-6 max-w-xl mx-auto">
            <BeforeAfterSlider before={resolveImg(project.before)} after={resolveImg(project.after)} />
          </div>
        </section>
      )}

      <section className="py-16 bg-black border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">Ook uw woning verbouwen?</h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
            <button onClick={() => navigate("/#contact")} data-testid="project-cta-quote" className="bg-[#FF1A1A] text-white px-8 py-4 font-semibold hover:bg-[#E60000] transition-colors flex items-center gap-2 justify-center">
              <FileText size={18} /> Gratis offerte
            </button>
            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="project-cta-call" className="border border-white/30 text-white px-8 py-4 font-semibold hover:bg-white hover:text-black transition-colors flex items-center gap-2 justify-center">
              <Phone size={18} /> Bel direct
            </a>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-8">Andere projecten</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {others.map((p) => (
                <motion.div key={p.slug} whileHover={{ y: -6 }} onClick={() => navigate(`/projecten/${p.slug}`)} data-testid={`other-project-${p.slug}`} className="relative h-72 overflow-hidden cursor-pointer group">
                  <img src={resolveImg(p.cover)} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
                  <div className="absolute bottom-6 left-6">
                    <span className="text-[#FF1A1A] text-sm font-semibold uppercase">{p.location}</span>
                    <h3 className="font-heading text-2xl font-bold text-white">{p.title}</h3>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
